import React, { ReactNode } from 'react';
import {
  Activity,
  ArrowBigRight,
  BaggageClaim,
  BanknoteArrowUp,
  BookmarkPlus,
  BookOpenText,
  BookUser,
  CalendarClock,
  CalendarDays,
  ChartArea,
  ChartLine,
  CircleDollarSign,
  Clock,
  ClockArrowDown,
  ClockFading,
  Cog,
  Container,
  DollarSign,
  FileChartLine,
  FileClock,
  FolderKanban,
  HandCoins,
  HeartHandshake,
  House,
  Key,
  LayoutDashboard,
  LayoutPanelTop,
  Lightbulb,
  ListCheck,
  ListChevronsDownUp,
  Lock,
  Mail,
  Map,
  MonitorStop,
  Phone,
  Plus,
  Receipt,
  ReceiptText,
  RotateCcwKey,
  ShieldCheck,
  Store,
  Ticket,
  TicketsPlane,
  TriangleAlert,
  UserPlus,
  UserRoundCog,
  Users,
  Wallet,
  Warehouse,
  Wrench,
  Building2,
  Building,
  HousePlug,
  Boxes,
  Settings2,
  BrickWallFire,
  Webhook,
  Waypoints,
} from 'lucide-react';
import { NavLink, useHistory } from 'react-router-dom';

import {
  clientDetails,
  CONFIG,
  isNoCodeLowCodeModuleEnabled,
  MODULES,
  PF_ADMIN,
} from '../../../../constants';
import { isRingCentralEnabled } from '../components/ringCentral/ringCentralConfig';
import permissions from '../../../../config/permissions';
import { REACT_APP_PF_ADMIN_ID } from '../../../../constants/envConstants';
import { CLIENT_ID } from '../../../../constants/client.constant';

export interface AppMenuItem {
  label: string | ReactNode;
  icon?: ReactNode;
  template?: (_item: AppMenuItem) => ReactNode;
  command?: () => void;
  permission?: string;
  className?: string;
  items?: AppMenuItem[];
  separator?: boolean;
  key?: string;
}
type RouterHistory = ReturnType<typeof useHistory>;

export const menuItemsList = (
  setDailogVisible: React.Dispatch<React.SetStateAction<boolean>>,
  clientId: string | null,
  permissionList: string,
  history: RouterHistory,
  pathname: string
): AppMenuItem[] => {
  const isActive = (path: string, pathname: string): boolean => {
    if (path === MODULES.ENTITY.BASE_ROUTE) {
      return pathname === path || pathname.startsWith(`${path}/`);
    }

    return pathname === path;
  };
  const isParentActive = (paths: string[], pathname: string): boolean =>
    paths.some(p => pathname.startsWith(p));

  const isEntityModuleEnabled = isNoCodeLowCodeModuleEnabled(clientDetails);

  const menuJson: AppMenuItem[] = [
    {
      label: 'Home & Help',
      icon: (
        <div className="root-icons">
          <House size="small" />
        </div>
      ),
      permission: 'HOME_M',
      className: `root-menu home-and-help ${
        isParentActive(
          [
            '/dashboard',

            '/getstarted',
            '/get-started',
            MODULES.SUPPORT.BASE_ROUTE,
          ],
          pathname
        )
          ? 'active-root'
          : ''
      }`,
      items: [
        {
          label: <NavLink to={'/dashboard'}>Dashboard</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-sm bg-purple-50">
              <LayoutDashboard className="text-purple-600" />
            </div>
          ),
          permission: 'HOME_M',
          className: `menu-level-first ${
            isActive('/dashboard', pathname) ? 'text-primary font-bold' : ''
          }`,
        },

        {
          label: (
            <NavLink to={MODULES.SUPPORT.BASE_ROUTE}>
              {MODULES.SUPPORT.TITLE}
            </NavLink>
          ),
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-sm bg-red-50">
              <Ticket size="small" className="text-green-600" />
            </div>
          ),
          permission: permissions.support.menu,
          className: `menu-level-first ${
            isActive(MODULES.SUPPORT.BASE_ROUTE, pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },

        {
          key: 'help_page_config',
          label: <NavLink to={'/get-started'}>Quick Start & Help</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-sm bg-primary-50">
              <Lightbulb size="small" className="text-primary-600" />
            </div>
          ),
          permission: 'HELP',
          className: `menu-level-first ${
            isActive('/get-started', pathname) ? 'text-primary font-bold' : ''
          }`,
        },

        {
          command: () =>
            window.open(
              'https://projectsforce.com/privacy-policy',
              '_blank',
              'noopener,noreferrer'
            ),
          label: 'Privacy Policy',
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-sm bg-orange-50">
              <Lock size="small" className="text-orange-600" />
            </div>
          ),
          permission: 'PERSONNEL_M',
          className: 'menu-level-first',
        },
      ],
    },

    {
      label: 'Add New',
      icon: (
        <div className="root-icons">
          <Plus size="small" />
        </div>
      ),
      permission: 'HOME_M',
      className: `root-menu add-new ${
        isParentActive(
          ['/crm/add/customer', '/project/add', '/project/chargeback/add'],
          pathname
        )
          ? 'active-root'
          : ''
      }`,
      items: [
        {
          label: <NavLink to={'/crm/add/customer'}>Client</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-primary-50">
              <UserPlus className="text-primary-600" />
            </div>
          ),
          permission: 'CLIENTS_M',
          className: `menu-level-first ${
            isActive('/crm/add/customer', pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },
        {
          label: <NavLink to={'/project/add'}>Project</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-green-50">
              <BookmarkPlus className="text-green-600" />
            </div>
          ),
          permission: 'ADD_PROJECT',
          className: `menu-level-first ${
            isActive('/project/add', pathname) ? 'text-primary font-bold' : ''
          }`,
        },
        {
          key: 'chargeback',
          label: <NavLink to={'/project/chargeback/add'}>Chargeback</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-orange-50">
              <Wallet className="text-orange-600" />
            </div>
          ),
          permission: 'ADD_APPROVE_CHARGEBACK',
          className: `menu-level-first ${
            isActive('/project/chargeback/add', pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },
      ],
    },

    {
      label: 'Project',
      icon: (
        <div className="root-icons">
          <FolderKanban size="small" />
        </div>
      ),
      permission: 'PROJECT_M',
      className: `root-menu project ${
        isParentActive(
          [
            '/scheduler',
            '/calendar',
            '/project/map',
            '/personnel/availabilityupdates',
            '/project/clients',
            '/project/personalized-emails-template',
            '/project/Quote-template',
            '/pull-home-depot-project',
            '/project/activities',
          ],
          pathname
        )
          ? 'active-root'
          : ''
      }`,
      items: [
        {
          label: <NavLink to={'/scheduler'}>Scheduler</NavLink>,

          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-sm bg-orange-50">
              <Clock className="text-orange-600" />
            </div>
          ),
          permission: 'SCHEDULER_M',
          className: `menu-level-first ${
            isActive('/scheduler', pathname) ? 'text-primary font-bold' : ''
          }`,
        },
        {
          label: <NavLink to={'/calendar'}>Calendar</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-bluegray-50">
              <CalendarDays className="text-bluegray-600" />
            </div>
          ),
          permission: 'VIEW_CALENDAR',
          className: `menu-level-first ${
            isActive('/calendar', pathname) ? 'text-primary font-bold' : ''
          }`,
        },
        {
          label: <NavLink to={'/project/map'}>Map</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-green-50">
              <Map className="text-green-600" />
            </div>
          ),
          permission: 'MAP_VIEW',
          className: `menu-level-first ${
            isActive('/project/map', pathname) ? 'text-primary font-bold' : ''
          }`,
        },
        {
          label: (
            <NavLink to={'/personnel/availabilityupdates'}>
              Availability Updates
            </NavLink>
          ),
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-red-50">
              <CalendarClock className="text-red-600" />
            </div>
          ),
          permission: 'AVAILABILITY_UPDATE_M',
          className: `menu-level-first ${
            isActive('/personnel/availabilityupdates', pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },
        ...(CONFIG.ENABLE_CUSTOMER_LIST === '1'
          ? [
              {
                label: <NavLink to={'/project/clients'}>Clients</NavLink>,
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-orange-50">
                    <Users className="text-orange-600" />
                  </div>
                ),
                permission: 'CLIENTS_M',
                className: `menu-level-first ${
                  isActive('/project/clients', pathname)
                    ? 'text-primary font-bold'
                    : ''
                }`,
              },
            ]
          : []),
        ...((parseInt(clientDetails?.personalized_emails) === 1 ||
          parseInt(clientDetails?.crm_module) === 1 ||
          permissions.templateConfiguration?.view) &&
        permissionList?.includes(permissions.template.view)
          ? [
              {
                label: 'Templates',
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-primary-50">
                    <LayoutPanelTop className="text-primary-600" />
                  </div>
                ),
                permission: 'TEMPLATE_VIEW',
                className: `menu-level-first submenu-hover-bg-none mobile-menu ${
                  isParentActive(
                    [
                      '/project/personalized-emails-template',
                      '/project/Quote-template',
                    ],
                    pathname
                  )
                    ? 'active-root'
                    : ''
                }`,
                items: [
                  ...(parseInt(clientDetails?.personalized_emails) === 1
                    ? [
                        {
                          label: (
                            <NavLink
                              to={'/project/personalized-emails-template'}
                            >
                              Email
                            </NavLink>
                          ),

                          icon: (
                            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-red-50">
                              <Mail className="text-red-600" />
                            </div>
                          ),
                          permission: 'EMAIL_TEMPLATE_VIEW',
                          className: `menu-level-first ${
                            isActive(
                              '/project/personalized-emails-template',
                              pathname
                            )
                              ? 'text-primary font-bold'
                              : ''
                          }`,
                        },
                      ]
                    : []),
                  ...(parseInt(clientDetails?.crm_module) === 1
                    ? [
                        {
                          label: (
                            <NavLink to={'/project/Quote-template'}>
                              Quote
                            </NavLink>
                          ),
                          icon: (
                            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-purple-50">
                              <ReceiptText className="text-purple-600" />
                            </div>
                          ),
                          permission: 'QUOTE_TEMPLATE_VIEW',
                          className: `menu-level-first ${
                            isActive('/project/Quote-template', pathname)
                              ? 'text-primary font-bold'
                              : ''
                          }`,
                        },
                      ]
                    : []),

                  {
                    label: (
                      <NavLink to={'/project/dynamic-action-button'}>
                        Action Buttons
                      </NavLink>
                    ),
                    icon: (
                      <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-purple-50">
                        <ArrowBigRight className="text-green-600" />
                      </div>
                    ),
                    permission: 'VIEW_ACTION_BTN_TEMP_CONFIG',
                    className: `menu-level-first ${
                      isActive('/project/dynamic-action-button', pathname)
                        ? 'text-primary font-bold'
                        : ''
                    }`,
                  },
                ],
              },
            ]
          : []),
        {
          label: (
            <NavLink
              to={'/pull-home-depot-project'}
              onClick={e => {
                e.preventDefault();
                setDailogVisible(true);
              }}
            >
              Pull Home Depot Project
            </NavLink>
          ),
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-purple-50">
              <Wrench className="text-purple-600" />
            </div>
          ),
          permission: 'PULL_PROJECT_FROM_SOURCE',
          className: `menu-level-first ${
            isActive('/pull-home-depot-project', pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },
        ...(parseInt(clientDetails?.project_activity_workflow) === 1
          ? [
              {
                label: (
                  <NavLink to={'/project/activities'}>
                    Activity Workflow
                  </NavLink>
                ),
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-green-50">
                    <FileChartLine className="text-green-600" />
                  </div>
                ),
                permission: 'PROJECT_M',
                className: `menu-level-first ${
                  isActive('/project/activities', pathname)
                    ? 'text-primary font-bold'
                    : ''
                }`,
              },
            ]
          : []),
      ],
    },
    ...(isEntityModuleEnabled &&
    clientId !== CLIENT_ID.INTERFACE &&
    clientId !== CLIENT_ID.PF_VALIDATION
      ? [
          {
            label: <NavLink to={'/entity'}>Dynamic Forms</NavLink>,
            icon: (
              <div className="root-icons">
                <Building2 size="small" />
              </div>
            ),
            permission: 'HOME_M',
            className: `root-menu ${
              isParentActive(['/entity'], pathname) ? 'active-root' : ''
            }`,
          },
        ]
      : []),
    ...(clientId === CLIENT_ID.INTERFACE || clientId === CLIENT_ID.PF_VALIDATION
      ? [
          {
            label: 'Dynamic Forms',
            icon: (
              <div className="root-icons">
                <Building2 size="small" />
              </div>
            ),
            permission: 'HOME_M',
            className: `root-menu ${
              isParentActive(
                [
                  '/brand',
                  '/architecture',
                  '/masterpart',
                  '/admin/historical-forecast-settings',
                  '/admin/fire-design-request',
                  '/admin/system-design',
                  '/admin/install-guide-request',
                  '/entity',
                ],
                pathname
              )
                ? 'active-root'
                : ''
            }`,
            items: [
              {
                label: <NavLink to={'/brand'}>Brands</NavLink>,
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-green-50">
                    <Building className="text-yellow-900" />
                  </div>
                ),
                permission: 'HOME_M',
                className: `menu-level-first ${
                  isActive('/brand', pathname) ? 'text-primary font-bold' : ''
                }`,
              },

              {
                label: (
                  <NavLink to={'/architecture'}>Architecture Types</NavLink>
                ),
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-green-50">
                    <HousePlug className="text-pink-500" />
                  </div>
                ),
                permission: 'HOME_M',
                className: `menu-level-first ${
                  isActive('/architecture', pathname)
                    ? 'text-primary font-bold'
                    : ''
                }`,
              },

              {
                label: <NavLink to={'/masterpart'}>Master Parts</NavLink>,
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-green-50">
                    <Boxes className="text-teal-700" />
                  </div>
                ),
                permission: 'HOME_M',
                className: `menu-level-first ${
                  isActive('/masterpart', pathname)
                    ? 'text-primary font-bold'
                    : ''
                }`,
              },

              {
                label: (
                  <NavLink to={'/admin/historical-forecast-settings'}>
                    Historical Forecast
                  </NavLink>
                ),
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-green-50">
                    <Settings2 className="text-green-600" />
                  </div>
                ),
                permission: 'HOME_M',
                className: `menu-level-first ${
                  isActive('/admin/historical-forecast-settings', pathname)
                    ? 'text-primary font-bold'
                    : ''
                }`,
              },

              {
                label: (
                  <NavLink to={'/admin/fire-design-request'}>
                    Fire Design Requests
                  </NavLink>
                ),
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-cyan-50">
                    <BrickWallFire className="text-orange-600" />
                  </div>
                ),
                permission: 'HOME_M',
                className: `menu-level-first ${
                  isActive('/admin/fire-design-request', pathname)
                    ? 'text-primary font-bold'
                    : ''
                }`,
              },

              {
                label: (
                  <NavLink to={'/admin/system-design'}>System Designs</NavLink>
                ),
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-cyan-50">
                    <Webhook className="text-pink-500" />
                  </div>
                ),
                permission: 'HOME_M',
                className: `menu-level-first ${
                  isActive('/admin/system-design', pathname)
                    ? 'text-primary font-bold'
                    : ''
                }`,
              },

              {
                label: (
                  <NavLink to={'/admin/install-guide-request'}>
                    Install Guide Requests
                  </NavLink>
                ),
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-cyan-50">
                    <Waypoints className="text-orange-600" />
                  </div>
                ),
                permission: 'HOME_M',
                className: `menu-level-first ${
                  isActive('/admin/install-guide-request', pathname)
                    ? 'text-primary font-bold'
                    : ''
                }`,
              },
              {
                label: (
                  <NavLink to={MODULES.ENTITY.BASE_ROUTE}>
                    {MODULES.ENTITY.TITLE}
                  </NavLink>
                ),
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-sm bg-cyan-50">
                    <Boxes size="small" className="text-cyan-600" />
                  </div>
                ),
                permission: 'HOME_M',
                className: `menu-level-first ${
                  isActive(MODULES.ENTITY.BASE_ROUTE, pathname)
                    ? 'text-primary font-bold'
                    : ''
                }`,
              },
            ],
          },
        ]
      : []),
    {
      label: 'Inventory & Vendors',
      icon: (
        <div className="root-icons">
          <Container size="small" />
        </div>
      ),
      permission: 'ADMIN_M',
      className: `root-menu inventory-vendors ${
        isParentActive(
          [
            '/project/items',
            '/admin/warehouse',
            '/project/vendormanagement',
            '/project/general-orders',
          ],
          pathname
        )
          ? 'active-root'
          : ''
      }`,
      items: [
        {
          label: <NavLink to={'/project/items'}>Items</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-red-50">
              <ListChevronsDownUp className="text-red-600" />
            </div>
          ),
          permission: 'ITEMS_M',
          className: `menu-level-first ${
            isActive('/project/items', pathname) ? 'text-primary font-bold' : ''
          }`,
        },
        {
          label: <NavLink to={'/admin/warehouse'}>Warehouse</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-purple-50">
              <Warehouse className="text-purple-600" />
            </div>
          ),
          permission: 'LABOR_PROFILES_M',
          className: `menu-level-first ${
            isActive('/admin/warehouse', pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },

        ...(parseInt(clientDetails?.vendor_purchase_order) === 1
          ? [
              {
                label: (
                  <NavLink to={'/project/vendormanagement'}>
                    Vendor Management
                  </NavLink>
                ),
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-orange-50">
                    <Store className="text-orange-600" />
                  </div>
                ),
                permission: 'VENDOR_MANAGEMENT_VIEW',
                className: `menu-level-first ${
                  isActive('/project/vendormanagement', pathname)
                    ? 'text-primary font-bold'
                    : ''
                }`,
              },
              {
                label: (
                  <NavLink to={'/project/general-orders'}>
                    General Purchase Orders
                  </NavLink>
                ),
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-bluegray-50">
                    <BaggageClaim className="text-bluegray-600" />
                  </div>
                ),
                permission: 'GENERAL_PURCHASE_ORDER_VIEW',
                className: `menu-level-first ${
                  isActive('/project/general-orders', pathname)
                    ? 'text-primary font-bold'
                    : ''
                }`,
              },
            ]
          : []),
      ],
    },

    {
      label: 'Admin',
      icon: (
        <div className="root-icons">
          <ShieldCheck size="small" />
        </div>
      ),
      permission: 'ADMIN_M',
      className: `root-menu administration ${
        isParentActive(
          [
            '/admin/stores',
            '/admin/usermanagement',
            '/admin/company-management',
            '/admin/municipality-management',
            '/admin/laborprofiles',
            '/admin/documentcenter',
            '/admin/checklist',
            '/data-import',
            '/admin/configuration',
            '/admin/help-page-config',
            '/admin/bulletin',
          ],
          pathname
        )
          ? 'active-root'
          : ''
      }`,
      items: [
        {
          label: <NavLink to={'/admin/stores'}>Stores</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-green-50">
              <Store className="text-green-600" />
            </div>
          ),
          permission: 'STORE_M',
          className: `menu-level-first ${
            isActive('/admin/stores', pathname) ? 'text-primary font-bold' : ''
          }`,
        },
        {
          label: (
            <NavLink to={'/admin/usermanagement'}>User Management</NavLink>
          ),
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-cyan-50">
              <UserRoundCog className="text-cyan-600" />
            </div>
          ),
          permission: 'USER_MGMT_M',
          className: `menu-level-first ${
            isActive('/admin/usermanagement', pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },
        {
          label: (
            <NavLink to={'/admin/company-management'}>
              Company Management
            </NavLink>
          ),
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-teal-50">
              <Building2 className="text-teal-600" />
            </div>
          ),
          permission: 'COMPANY_MANAGEMENT_VIEW',
          className: `menu-level-first ${
            isActive('/admin/company-management', pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },
        {
          label: (
            <NavLink to={'/admin/municipality-management'}>
              Municipality Registration
            </NavLink>
          ),
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-indigo-50">
              <Building2 className="text-indigo-600" />
            </div>
          ),
          permission: 'VIEW_MUNICIPALITY_REGISTRATION',
          className: `menu-level-first ${
            isActive('/admin/municipality-management', pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },
        {
          label: <NavLink to={'/admin/laborprofiles'}>Labor Profiles</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-yellow-50">
              <BookUser className="text-yellow-600" />
            </div>
          ),
          permission: 'LABOR_PROFILES_M',
          className: `menu-level-first ${
            isActive('/admin/laborprofiles', pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },
        {
          label: (
            <NavLink to={'/admin/documentcenter'}>Document Center</NavLink>
          ),
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-bluegray-50">
              <BookOpenText className="text-bluegray-600" />
            </div>
          ),
          permission: 'DOC_CENTER_M',
          className: `menu-level-first ${
            isActive('/admin/documentcenter', pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },
        {
          key: 'checklist',
          label: <NavLink to={'/admin/checklist'}>Checklist</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-yellow-50">
              <ListCheck className="text-yellow-600" />
            </div>
          ),
          permission: 'QUESTIONNAIRE_VIEW',
          className: `menu-level-first ${
            isActive('/admin/checklist', pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },
        {
          label: <NavLink to={'/data-import'}>Data Import</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-purple-50">
              <DollarSign className="text-purple-600" />
            </div>
          ),
          permission: 'UPLOAD_DATA_VIEW',
          className: `menu-level-first ${
            isActive('/data-import', pathname) ? 'text-primary font-bold' : ''
          }`,
        },
        {
          label: <NavLink to={'/admin/configuration'}>Configuration</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-red-50">
              <Cog className="text-red-600" />
            </div>
          ),
          permission: 'CONFIGURATION_M',
          className: `menu-level-first ${
            isActive('/admin/configuration', pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },
        ...(clientId === REACT_APP_PF_ADMIN_ID
          ? [
              {
                key: 'help_page_config',
                label: (
                  <NavLink to={'/admin/help-page-config'}>
                    Help Page Config
                  </NavLink>
                ),
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-blue-60">
                    <HeartHandshake className="text-blue-600" />
                  </div>
                ),
                permission: 'ADMIN_M',
                className: `menu-level-first ${
                  isActive('/admin/help-page-config', pathname)
                    ? 'text-primary font-bold'
                    : ''
                }`,
              },
            ]
          : []),
        {
          key: 'bulletin',
          label: <NavLink to={'/admin/bulletin'}>Bulletin</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-primary-50">
              <TriangleAlert className="text-primary-600" />
            </div>
          ),
          permission: 'CONFIGURATION_M',
          className: `menu-level-first ${
            isActive('/admin/bulletin', pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },
      ],
    },
    {
      label: 'Finance',
      icon: (
        <div className="root-icons">
          <CircleDollarSign size="small" />
        </div>
      ),
      permission: 'PROJECT_M',
      className: `root-menu finance ${
        isParentActive(
          [
            '/admin/payroll',
            '/admin/invoice-payments',
            '/payment-request',
            '/admin/tenant-billing',
          ],
          pathname
        )
          ? 'active-root'
          : ''
      }`,
      items: [
        {
          key: 'payroll',
          label: <NavLink to={'/admin/payroll'}>Payroll</NavLink>,

          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-green-50">
              <HandCoins className="text-green-600" />
            </div>
          ),
          permission: 'VIEW_PAYROLL',
          className: `menu-level-first ${
            isActive('/admin/payroll', pathname) ? 'text-primary font-bold' : ''
          }`,
        },
        ...(parseInt(clientDetails?.technician_invoice_guardrails) === 1
          ? [
              {
                key: 'invoice_payments',
                label: (
                  <NavLink to={'/admin/invoice-payments'}>
                    Invoice Payments
                  </NavLink>
                ),
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-blue-50">
                    <ReceiptText className="text-blue-600" />
                  </div>
                ),
                permission: 'VIEW_INVOICE_PAYMENTS',
                className: `menu-level-first ${
                  isActive('/admin/invoice-payments', pathname)
                    ? 'text-primary font-bold'
                    : ''
                }`,
              },
            ]
          : []),
        {
          label: <NavLink to={'/payment-request'}>Payment Request</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-orange-xs bg-green-50">
              <BanknoteArrowUp className="text-orange-600" />
            </div>
          ),
          permission: 'PAYMENT_REQUEST',
          className: `menu-level-first ${
            isActive('/payment-request', pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },
        {
          key: 'tenant_billing',
          label: <NavLink to={'/admin/tenant-billing'}>Tenant Billing</NavLink>,

          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-orange-50">
              <Receipt className="text-bluegray-600" />
            </div>
          ),
          permission: 'ADMIN_M',
          className: `menu-level-first ${
            isActive('/admin/tenant-billing', pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },
      ],
    },

    {
      label: 'Reports & Logs',
      icon: (
        <div className="root-icons">
          <FileClock size="small" />
        </div>
      ),
      permission: 'ADMIN_M',
      className: `root-menu reports-logs ${
        isParentActive(
          [
            '/admin/dashboards',
            '/admin/widgets',
            '/admin/export/reports',
            '/logs/customer-login-logs',
            '/logs/customer-register-logs',
            '/logs/customer-reset-password-logs',
            '/logs/login-logs',
            '/logs/reset-password-logs',
            '/admin/ruleenginelogs',
            '/admin/user-activity',
            '/call-history',
            '/email-tracking',
          ],
          pathname
        )
          ? 'active-root'
          : ''
      }`,
      items: [
        {
          label: 'Reports',
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-sm bg-purple-50">
              <ChartArea className="text-purple-600" />
            </div>
          ),
          permission: 'ADMIN_M',
          className: `menu-level-first mobile-menu ${
            isParentActive(
              [
                '/admin/dashboards',
                '/admin/widgets',
                '/admin/export/reports',
                '/email-tracking',
                '/call-history',
              ],
              pathname
            )
              ? 'active-root'
              : ''
          }`,
          items: [
            {
              label: <NavLink to={'/admin/dashboards'}>Dashboards</NavLink>,
              icon: (
                <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-red-50">
                  <MonitorStop className="text-red-600" />
                </div>
              ),
              permission: 'DASHBOARD_MGMT_M',
              className: `menu-level-first ${
                isActive('/admin/dashboards', pathname)
                  ? 'text-primary font-bold'
                  : ''
              }`,
            },
            {
              label: <NavLink to={'/admin/widgets'}>Widgets</NavLink>,
              icon: (
                <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-indigo-50">
                  <ChartLine className="text-indigo-600" />
                </div>
              ),
              permission: 'DASHBOARD_WIDGET_TEMPLATE',
              className: `menu-level-first ${
                isActive('/admin/widgets', pathname)
                  ? 'text-primary font-bold'
                  : ''
              }`,
            },
            {
              key: 'exported_reports',
              label: (
                <NavLink to={'/admin/export/reports'}>Exported Reports</NavLink>
              ),
              icon: (
                <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-bluegray-50">
                  <TicketsPlane className="text-bluegray-600" />
                </div>
              ),
              permission: 'VIEW_PAYROLL',
              className: `menu-level-first ${
                isActive('/admin/export/reports', pathname)
                  ? 'text-primary font-bold'
                  : ''
              }`,
            },
            {
              key: 'email_tracking',
              label: <NavLink to={'/email-tracking'}>Email Tracking</NavLink>,
              icon: (
                <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-bluegray-50">
                  <MonitorStop className="text-bluegray-600" />
                </div>
              ),
              permission: 'EMAIL_EVENTS_TRACKING',
              className: `menu-level-first ${
                isActive('/email-tracking', pathname)
                  ? 'text-primary font-bold'
                  : ''
              }`,
            },
            {
              key: 'call_history',
              label: <NavLink to={'/call-history'}>Call History</NavLink>,
              icon: (
                <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-blue-50">
                  <Phone className="text-blue-600" />
                </div>
              ),
              permission: 'HOME_M',
              className: `menu-level-first ${
                isActive('/call-history', pathname)
                  ? 'text-primary font-bold'
                  : ''
              }`,
            },
          ],
        },
        ...(clientId === PF_ADMIN
          ? [
              {
                label: 'Logs',
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-sm bg-orange-50">
                    <ClockFading className="text-orange-600" />
                  </div>
                ),
                permission: 'HOME_M',
                className: `menu-level-first mobile-menu ${
                  isParentActive(
                    [
                      '/logs/customer-login-logs',
                      '/logs/customer-register-logs',
                      '/logs/customer-reset-password-logs',
                      '/logs/login-logs',
                      '/logs/reset-password-logs',
                    ],
                    pathname
                  )
                    ? 'active-root'
                    : ''
                }`,
                items: [
                  {
                    label: 'Customer Portal',
                    icon: (
                      <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-red-50">
                        <MonitorStop className="text-red-600" />
                      </div>
                    ),
                    permission: 'HOME_M',
                    className: `menu-level-first ${
                      isParentActive(
                        [
                          '/logs/customer-login-logs',
                          '/logs/customer-register-logs',
                          '/logs/customer-reset-password-logs',
                        ],
                        pathname
                      )
                        ? 'active-root'
                        : ''
                    }`,
                    items: [
                      ...(clientId === PF_ADMIN
                        ? [
                            {
                              key: 'customer_login_logs',
                              label: (
                                <NavLink to={'/logs/customer-login-logs'}>
                                  Login
                                </NavLink>
                              ),
                              icon: (
                                <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-primary-50">
                                  <Key className="text-primary-600" />
                                </div>
                              ),
                              permission: 'HOME_M',
                              className: `menu-level-first ${
                                isActive('/logs/customer-login-logs', pathname)
                                  ? 'text-primary font-bold'
                                  : ''
                              }`,
                            },
                            {
                              key: 'customer_register_logs',
                              label: (
                                <NavLink to={'/logs/customer-register-logs'}>
                                  Register
                                </NavLink>
                              ),
                              icon: (
                                <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-bluegray-50">
                                  <BookUser className="text-bluegray-600" />
                                </div>
                              ),
                              permission: 'HOME_M',
                              className: `menu-level-first ${
                                isActive(
                                  '/logs/customer-register-logs',
                                  pathname
                                )
                                  ? 'text-primary font-bold'
                                  : ''
                              }`,
                            },
                            {
                              key: 'customer_reset_password_logs',
                              label: (
                                <NavLink
                                  to={'/logs/customer-reset-password-logs'}
                                >
                                  Reset Password
                                </NavLink>
                              ),
                              icon: (
                                <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-pink-50">
                                  <RotateCcwKey className="text-pink-600" />
                                </div>
                              ),
                              permission: 'HOME_M',
                              className: `menu-level-first ${
                                isActive(
                                  '/logs/customer-reset-password-logs',
                                  pathname
                                )
                                  ? 'text-primary font-bold'
                                  : ''
                              }`,
                            },
                          ]
                        : []),
                    ],
                  },

                  {
                    label: 'Admin Portal',
                    icon: (
                      <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-green-50">
                        <UserRoundCog className="text-green-600" />
                      </div>
                    ),
                    permission: 'HOME_M',
                    className: `menu-level-first ${
                      isParentActive(
                        ['/logs/login-logs', '/logs/reset-password-logs'],
                        pathname
                      )
                        ? 'active-root'
                        : ''
                    }`,
                    items: [
                      ...(clientId === PF_ADMIN
                        ? [
                            {
                              key: 'login_logs',
                              label: (
                                <NavLink to={'/logs/login-logs'}>Login</NavLink>
                              ),
                              icon: (
                                <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-bluegray-50">
                                  <Key className="text-bluegray-600" />
                                </div>
                              ),
                              permission: 'HOME_M',
                              className: `menu-level-first ${
                                isActive('/logs/login-logs', pathname)
                                  ? 'text-primary font-bold'
                                  : ''
                              }`,
                            },
                            {
                              key: 'reset_passowrd_logs',
                              label: (
                                <NavLink to={'/logs/reset-password-logs'}>
                                  Reset Password
                                </NavLink>
                              ),
                              icon: (
                                <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-purple-50">
                                  <RotateCcwKey className="text-purple-600" />
                                </div>
                              ),
                              permission: 'HOME_M',
                              className: `menu-level-first ${
                                isActive('/logs/reset-password-logs', pathname)
                                  ? 'text-primary font-bold'
                                  : ''
                              }`,
                            },
                          ]
                        : []),
                    ],
                  },
                ],
              },
            ]
          : []),
        {
          label: <NavLink to={'/admin/ruleenginelogs'}>Event Logs</NavLink>,
          icon: (
            <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-purple-50">
              <ClockArrowDown />
            </div>
          ),
          permission: 'RULE_ENGINE_LOGS_VIEW',
          className: `menu-level-first ${
            isActive('/admin/ruleenginelogs', pathname)
              ? 'text-primary font-bold'
              : ''
          }`,
        },
        ...(parseInt(clientDetails?.vendor_purchase_order) === 1
          ? [
              {
                key: 'user_activity',
                label: (
                  <NavLink to={'/admin/user-activity'}>User Activity</NavLink>
                ),
                icon: (
                  <div className="icons flex justify-content-center align-items-center w-2rem h-2rem border-round-xs bg-indigo-50">
                    <Activity className="text-indigo-600" />
                  </div>
                ),
                permission: 'USER_ACTIVITY',
                className: `menu-level-first ${
                  isActive('/admin/user-activity', pathname)
                    ? 'text-primary font-bold'
                    : ''
                }`,
              },
            ]
          : []),
      ],
    },
  ];
  const filteredMenuItems = menuJson?.filter(menu => {
    if (!menu) return false;

    if (Array.isArray(menu.items) && menu.items?.length > 0) {
      const filteredSubMenus = menu.items?.filter(submenu => {
        if (!submenu) return false;

        if (submenu?.key === 'bulletin' || submenu?.key === 'tenant_billing') {
          return clientId === PF_ADMIN;
        }

        if (Array.isArray(submenu.items) && submenu.items?.length > 0) {
          submenu.items = submenu.items?.filter(item => {
            if (item?.key === 'call_history') {
              return isRingCentralEnabled();
            }
            return permissionList.includes(item.permission as string);
          });
          return submenu.items.length > 0;
        }

        return (
          permissionList.includes(submenu.permission as string) ||
          submenu.permission === 'HELP'
        );
      });

      menu.items = filteredSubMenus;

      return filteredSubMenus.length > 0;
    }

    return (
      permissionList?.includes(menu.permission as string) ||
      menu.items?.some(item => item.permission === 'HELP')
    );
  });

  // const filteredMenuItems = menuJson?.filter(menu => {
  //   if (!menu) return false;

  //   const hasMenuPermission =
  //     permissionList?.includes(menu.permission as string) ||
  //     menu.items?.some(item => item.permission === 'HELP');

  //   if (hasMenuPermission) {
  //     if (menu.items) {
  //       menu.items = menu.items.filter(submenu => {
  //         if (!submenu) return false;

  //         if (submenu.key === 'bulletin') return clientId === PF_ADMIN;

  //         if (Array.isArray(submenu.items) && submenu.items.length > 0) {
  //           submenu.items = submenu.items.filter(item =>
  //             permissionList.includes(item.permission as string)
  //           );
  //           return submenu.items.length > 0;
  //         }

  //         if (submenu.key === 'tenant_billing') return clientId === PF_ADMIN;

  //         return (
  //           permissionList.includes(submenu.permission as string) ||
  //           submenu.permission === 'HELP'
  //         );
  //       });
  //     }
  //     return true;
  //   }
  //   return false;
  // });
  return filteredMenuItems;
};

export default menuItemsList;

export const themeColors = [
  {
    label: 'Blue',
    value: 'light-blue',
    color:
      'linear-gradient(90deg, rgb(169 218 226) 0%, #4bc0d1 50%, rgb(174 220 227) 100%)',
  },

  {
    label: 'Purple',
    value: 'light-purple',
    color:
      'linear-gradient(90deg, rgb(204 166 232) 0%, rgb(174 107 225) 50%, rgb(204 165 232) 100%)',
  },
  {
    label: 'Sunset',
    value: 'sunset',
    color:
      'linear-gradient(90deg, rgb(211 160 139) 0%, rgb(205 110 72) 50%, rgb(209 155 132) 100%)',
  },
  {
    label: 'Green',
    value: 'green',
    color: '#81ce65',
  },

  {
    label: 'Pink',
    value: 'pink',
    color:
      'linear-gradient(90deg, rgb(248 167 217) 0%, rgb(255 162 220) 50%, rgb(255 181 227) 100%)',
  },
  {
    label: 'Teal',
    value: 'teal',
    color: '#55ccc0',
  },
];

export const THEME_CLASSES = [
  'light-blue',
  'light-purple',
  'sunset',
  'green',
  'pink',
  'teal',
];
